import { Button, Callout, Card, Divider, FormGroup, HTMLSelect, Intent, Popover } from '@blueprintjs/core'
import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { establishQZConnection, setCurrentPrinterType, setTestPrintType } from '../../current/actions'
import PrinterList from '../../routes/settings/printing/PrinterList'
import { AppState } from '../../store'
import PrinterV2 from '../printer'
import { PrintLabelFormat, TestPrintType } from '../types'
import ScalePicker from './ScalePicker'
import ConveyorPortPicker from './ConveyorPortPicker'
import hasFeature from '../../featureFlags'
import config from '../../config'

const Config: React.FC = () => {
  const qzConnection = useSelector((s: AppState) => s.current.qzConnection)
  const currentPrinterType = useSelector((s: AppState) => s.current.currentPrinterType)
  const testPrintType = useSelector((s: AppState) => s.current.testPrintType)
  const dispatch = useDispatch()
  const choosePortFeature = hasFeature('clothing-conveyor')
  const booksFeature = config.LISTER_BOOKS()
  const ReconnectBtn = () => (
    <Button
      intent="primary"
      loading={qzConnection.loading}
      onClick={() => {
        dispatch(establishQZConnection())
        analytics.track('ListerConnect Reconnect Clicked')
      }}
    >
      Reconnect
    </Button>
  )

  const onTestPrint = () => {
    const printer = new PrinterV2()

    if (testPrintType === 'barcode') {
      printer.printProductLabelRaw([
        {
          sku: '2019561246',
          productName: 'Predominately Black Patagonia',
          supplierName: 'Q-Truck',
          inventoryLocationName: '10-A',
          date: new Date().toLocaleDateString()
        }
      ])
    } else if (testPrintType === 'shipping') {
      printer.printTestShippingLabel()
    } else if (testPrintType === 'media') {
      const dateString = new Date().toLocaleDateString()
      const timeString = new Date().toLocaleTimeString([], { hour: 'numeric', minute: 'numeric' })
      printer.printMediaTicketRaw({
        shelfIdentifier: 'C 031',
        title: '2001: A Space Odyssey',
        condition: 'Very Good',
        supplier: 'Alexandria',
        sku: '48R5MENK5',
        uid: 'a51796b28513', // aligns with InventoryUnit.generate_unique_uid
        createdBy: 'david',
        asin: '0451457994',
        dateAdded: `${dateString} at ${timeString}`
      })
    }
  }

  if (qzConnection.loading) {
    return (
      <>
        <Callout title="Connecting..." intent="warning" icon="feed" style={{ marginBottom: 5 }} />
        <p className="bp5-ui-text bp5-text-muted" style={{ fontStyle: 'italic' }}>
          This may take a few seconds
        </p>

        <ReconnectBtn />
      </>
    )
  }

  // It's a shame, but we're going to keep ESC/POS disabled.
  // The Star TSP700ii (lots of customers have this) supports ESC/POS mode and Starline mode,
  // but their Windows driver only supports Starline mode.
  // We're going to recommend they print with PDF label format.
  const escEnabled = booksFeature && false

  let helperText = 'Use PDF for Dymo and ZPL for Zebra'
  if (booksFeature) {
    helperText = 'Use PDF for Dymo/Star and ZPL for Zebra'
  }

  return (
    <>
      {qzConnection.connected ? (
        <>
          <Callout title="Connected!" intent="success" icon="feed-subscribed" style={{ marginBottom: 10 }} />
          <PrinterList />
          <FormGroup label="Label format" helperText={helperText}>
            <HTMLSelect
              value={currentPrinterType}
              onChange={e => dispatch(setCurrentPrinterType(e.target.value as PrintLabelFormat))}
            >
              <option value="PDF">{booksFeature ? 'PDF (Dymo/Star)' : 'PDF (Dymo)'}</option>
              <option value="ZPL">ZPL (Zebra)</option>
              {escEnabled && <option value="ESC">ESC/POS (Star)</option>}
            </HTMLSelect>
          </FormGroup>
          <ScalePicker />
          {choosePortFeature && <ConveyorPortPicker />}
          <FormGroup label="Test print">
            <HTMLSelect
              value={testPrintType}
              onChange={e => dispatch(setTestPrintType(e.target.value as TestPrintType))}
              options={
                booksFeature
                  ? [
                      { label: 'Barcode (3.5x1.125")', value: 'barcode' },
                      { label: 'Shipping Label (4x6")', value: 'shipping' },
                      { label: 'Media ticket (80mm)', value: 'media' }
                    ]
                  : [
                      { label: 'Barcode (3.5x1.125")', value: 'barcode' },
                      { label: 'Shipping Label (4x6")', value: 'shipping' }
                    ]
              }
            />
            <Button minimal icon="print" onClick={onTestPrint} />
          </FormGroup>
        </>
      ) : (
        <>
          <Callout title="Could not connect" intent="danger" icon="offline" style={{ marginBottom: 10 }} />
          <p className="bp5-ui-text bp5-text-muted" style={{ fontStyle: 'italic', maxWidth: 315 }}>
            Make sure Lister Connect is running locally on your computer
          </p>
          <ReconnectBtn />
        </>
      )}

      <Divider style={{ margin: '15px 5px 10px 5px' }} />
      <a
        href="https://help.uprightlabs.com/en-us/lister/printers-and-scales#main-content"
        target="_blank"
        rel="noopener noreferrer"
      >
        Get help
      </a>
    </>
  )
}

const PrintNavbarSettings: React.FC = () => {
  const listerConnectEnabled = useSelector((s: AppState) => s.current.organization.settings.listerConnectEnabled)
  const qzConnection = useSelector((s: AppState) => s.current.qzConnection)
  const dispatch = useDispatch()

  React.useEffect(() => {
    if (listerConnectEnabled) {
      dispatch(establishQZConnection())
    }
  }, [dispatch, listerConnectEnabled])

  let intent: Intent = qzConnection.connected ? 'success' : 'danger'
  if (qzConnection.loading) intent = 'warning'

  if (!listerConnectEnabled) return null

  return (
    <Popover
      portalClassName="print-navbar-portal"
      lazy
      interactionKind="click"
      content={
        <Card elevation={1} style={{ padding: 10, minWidth: 315 }}>
          <Config />
        </Card>
      }
      position="bottom-right"
      minimal
    >
      <Button icon="print" minimal intent={intent} />
    </Popover>
  )
}
export default PrintNavbarSettings
